import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useToast } from '../context/ToastContext';
import { useTranslation } from '../i18n/useTranslation';
import DatePicker from './DatePicker';
import DateRangePicker from './DateRangePicker';
import TimePicker from './TimePicker';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import * as XLSX from 'xlsx';
import AdminPricing from './AdminPricing';
import AdminUsers from './AdminUsers';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Collapsible section used by the CMS tab.
const CmsSection = ({ id, title, description, open, onToggle, isRTL, children }) => (
  <div className="bg-white rounded-lg shadow-md overflow-hidden">
    <button
      type="button"
      onClick={() => onToggle(open ? null : id)}
      aria-expanded={open}
      className="w-full flex items-center justify-between gap-3 p-4 sm:p-6 text-start hover:bg-gray-50 transition"
    >
      <span className="min-w-0">
        <span className="block text-base sm:text-lg font-semibold text-gray-900">{title}</span>
        {description && (
          <span className="block text-xs sm:text-sm text-gray-600 mt-0.5">{description}</span>
        )}
      </span>
      <svg
        className={`w-5 h-5 flex-shrink-0 text-gray-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    </button>
    {open && (
      <div className="px-4 sm:px-6 pb-4 sm:pb-6 space-y-4 border-t border-gray-100 pt-4" dir={isRTL ? 'rtl' : 'ltr'}>
        {children}
      </div>
    )}
  </div>
);

const defaultFooterConfig = {
  workingHours: {
    title: {
      en: "Doctor's Working Hours:",
      ar: 'أوقات دوام الدكتورة :'
    },
    days: {
      en: 'Sunday - Monday - Wednesday only',
      ar: 'الأحد - الأثنين - الأربعاء فقط'
    },
    time: {
      en: 'From 1:00 PM to 5:00 PM',
      ar: 'من الساعة ١ مساءً حتى ٥ مساءً'
    }
  },
  contact: {
    phone: '',
    phoneDisplay: '',
    whatsapp: '',
    email: '',
    instagram: '',
    snapchat: '',
    tiktok: '',
    twitter: ''
  },
  location: {
    address: {
      en: 'Riyadh, Saudi Arabia',
      ar: 'الرياض، المملكة العربية السعودية'
    },
    mapUrl: '',
    mapEmbedUrl: ''
  }
};

const normalizeFooterConfig = (config = {}) => {
  const hours = config.workingHours || {};
  const location = config.location || {};
  return {
    workingHours: {
      title: { ...defaultFooterConfig.workingHours.title, ...(hours.title || {}) },
      days: { ...defaultFooterConfig.workingHours.days, ...(hours.days || {}) },
      time: { ...defaultFooterConfig.workingHours.time, ...(hours.time || {}) }
    },
    contact: { ...defaultFooterConfig.contact, ...(config.contact || {}) },
    location: {
      ...defaultFooterConfig.location,
      ...location,
      address: { ...defaultFooterConfig.location.address, ...(location.address || {}) }
    }
  };
};

const defaultSiteAccessConfig = {
  enabled: false,
  mode: 'maintenance',
  messages: {
    maintenance: {
      en: 'The site is currently under maintenance. Please check back later.',
      ar: 'الموقع تحت الصيانة حالياً. يرجى المحاولة لاحقاً.'
    },
    vacation: {
      en: 'Schedules are not available currently as the doctor is on annual vacation. Please check back later.',
      ar: 'المواعيد غير متاحة حالياً لأن الطبيبة في إجازتها السنوية. يرجى المحاولة لاحقاً.'
    }
  }
};

const normalizeSiteAccessConfig = (config = {}) => ({
  ...defaultSiteAccessConfig,
  ...config,
  enabled: Boolean(config.enabled),
  mode: ['maintenance', 'vacation'].includes(config.mode) ? config.mode : defaultSiteAccessConfig.mode,
  messages: {
    maintenance: {
      ...defaultSiteAccessConfig.messages.maintenance,
      ...(config.messages?.maintenance || {})
    },
    vacation: {
      ...defaultSiteAccessConfig.messages.vacation,
      ...(config.messages?.vacation || {})
    }
  }
});

// Helper function to convert time string (HH:MM) to minutes
const timeToMinutes = (timeStr) => {
  const [hours, minutes] = timeStr.split(':').map(Number);
  return hours * 60 + minutes;
};

// Helper function to calculate days between dates for selected days of week
const calculateDaysBetween = (startDate, endDate, selectedDays) => {
  if (!startDate || !endDate) return 0;
  const start = new Date(startDate);
  const end = new Date(endDate);
  let count = 0;
  const current = new Date(start);
  while (current <= end) {
    if (selectedDays.includes(current.getDay())) {
      count++;
    }
    current.setDate(current.getDate() + 1);
  }
  return count;
};

const AdminDashboard = () => {
  const { showToast } = useToast();
  const { user: currentUser } = useAuth();
  // Content (CMS) and user management are super-admin only.
  const isSuper = currentUser?.role === 'super';
  const { t, language } = useTranslation();
  const isRTL = language === 'ar';
  const [activeTab, setActiveTab] = useState('slots');
  const [slots, setSlots] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [bookingsView, setBookingsView] = useState('table'); // 'cards' or 'table'
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  // Slots filtering and pagination
  const [slotsSearchTerm, setSlotsSearchTerm] = useState('');
  const [slotsStatusFilter, setSlotsStatusFilter] = useState('all');
  const [slotsSortBy, setSlotsSortBy] = useState('date');
  const [slotsSortOrder, setSlotsSortOrder] = useState('desc');
  const [slotsCurrentPage, setSlotsCurrentPage] = useState(1);
  const [slotsItemsPerPage, setSlotsItemsPerPage] = useState(10);

  // Unbook confirmation modal state
  const [unbookBooking, setUnbookBooking] = useState(null);

  // Import results modal state
  const [importResults, setImportResults] = useState(null);

  // Edit booking modal state
  const [editingBooking, setEditingBooking] = useState(null);
  const [editBookingDate, setEditBookingDate] = useState('');
  const [editBookingSlots, setEditBookingSlots] = useState([]);
  const [editBookingLoading, setEditBookingLoading] = useState(false);

  // Admin config state
  const [adminConfig, setAdminConfig] = useState(null);
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const [columnFilters, setColumnFilters] = useState({});
  const [columnSorts, setColumnSorts] = useState({});
  const [showExportModal, setShowExportModal] = useState(false);
  const [siteAccessForm, setSiteAccessForm] = useState(defaultSiteAccessConfig);
  const [footerForm, setFooterForm] = useState(defaultFooterConfig);
  const [openCmsSection, setOpenCmsSection] = useState('hours');

  // Format functions - must be defined before they're used in filtering/sorting
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    // Always use Gregorian calendar, but with Arabic month names if Arabic is selected
    const monthNames = language === 'ar'
      ? ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر']
      : ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const dayNames = language === 'ar'
      ? ['الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت']
      : ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

    const day = dayNames[date.getDay()];
    const month = monthNames[date.getMonth()];
    const dayNum = date.getDate();
    const year = date.getFullYear();

    return language === 'ar'
      ? `${day}، ${dayNum} ${month} ${year}`
      : `${day}, ${month} ${dayNum}, ${year}`;
  };

  const formatTime = (time) => {
    return new Date(`2000-01-01T${time}`).toLocaleTimeString(language === 'ar' ? 'ar-SA' : 'en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Get all unique field names from bookings for table columns
  const getAllFieldNames = () => {
    const fieldNames = new Set();
    bookings.forEach(booking => {
      if (booking.bookingFields) {
        // Handle Map type (from Mongoose)
        if (booking.bookingFields instanceof Map) {
          booking.bookingFields.forEach((value, key) => {
            if (key && key.trim() !== '') {
              fieldNames.add(key);
            }
          });
        } 
        // Handle regular object (from JSON)
        else if (typeof booking.bookingFields === 'object' && booking.bookingFields !== null) {
          Object.keys(booking.bookingFields).forEach(key => {
            if (key && key.trim() !== '') {
              fieldNames.add(key);
            }
          });
        }
      }
    });
    return Array.from(fieldNames).sort();
  };

  // Get visible columns based on config
  const getVisibleColumns = () => {
    if (!adminConfig?.bookingsTableConfig?.visibleColumns || adminConfig.bookingsTableConfig.visibleColumns.length === 0) {
      return ['date', 'time', 'name', 'email', 'status'];
    }
    return adminConfig.bookingsTableConfig.visibleColumns;
  };

  // Filter and sort bookings with per-column filters
  const getFilteredAndSortedBookings = () => {
    // Filter out bookings with null slots to prevent errors
    let filtered = bookings.filter(booking => booking.slot && booking.slot.date);

    // Global search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(booking => {
        const name = (booking.user?.name || booking.guestName || '').toLowerCase();
        const email = (booking.user?.email || booking.guestEmail || '').toLowerCase();
        // Handle null slots
        const date = booking.slot?.date ? formatDate(booking.slot.date).toLowerCase() : '';
        const time = booking.slot?.startTime && booking.slot?.endTime 
          ? `${booking.slot.startTime} - ${booking.slot.endTime}`.toLowerCase() 
          : '';
        return name.includes(search) || email.includes(search) || date.includes(search) || time.includes(search);
      });
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(booking => booking.status === statusFilter);
    }

    // Per-column filters
    Object.keys(columnFilters).forEach(columnKey => {
      const filterValue = columnFilters[columnKey];
      if (filterValue && filterValue.trim() !== '') {
        const searchLower = filterValue.toLowerCase();
        filtered = filtered.filter(booking => {
          switch (columnKey) {
            case 'date':
              return booking.slot?.date ? formatDate(booking.slot.date).toLowerCase().includes(searchLower) : false;
            case 'time':
              return booking.slot?.startTime && booking.slot?.endTime 
                ? `${booking.slot.startTime} - ${booking.slot.endTime}`.toLowerCase().includes(searchLower) 
                : false;
            case 'name':
              return (booking.user?.name || booking.guestName || '').toLowerCase().includes(searchLower);
            case 'email':
              return (booking.user?.email || booking.guestEmail || '').toLowerCase().includes(searchLower);
            case 'status':
              return booking.status.toLowerCase().includes(searchLower);
            default:
              // Custom field
              let fieldValue = '';
              if (booking.bookingFields) {
                if (booking.bookingFields instanceof Map) {
                  fieldValue = booking.bookingFields.get(columnKey) || '';
                } else if (typeof booking.bookingFields === 'object') {
                  fieldValue = booking.bookingFields[columnKey] || '';
                }
              }
              return fieldValue.toLowerCase().includes(searchLower);
          }
        });
      }
    });

    // Sort - check if there's a column-specific sort first
    const activeColumnSort = Object.keys(columnSorts).find(key => columnSorts[key]);
    if (activeColumnSort) {
      const sortOrder = columnSorts[activeColumnSort];
      filtered.sort((a, b) => {
        let aValue, bValue;
        
        switch (activeColumnSort) {
          case 'date':
            // Handle null slots
            if (!a.slot || !a.slot.date) return 1;
            if (!b.slot || !b.slot.date) return -1;
            aValue = new Date(a.slot.date).getTime();
            bValue = new Date(b.slot.date).getTime();
            break;
          case 'time':
            // Handle null slots
            if (!a.slot || !a.slot.startTime) return 1;
            if (!b.slot || !b.slot.startTime) return -1;
            aValue = timeToMinutes(a.slot.startTime);
            bValue = timeToMinutes(b.slot.startTime);
            break;
          case 'name':
            aValue = (a.user?.name || a.guestName || '').toLowerCase();
            bValue = (b.user?.name || b.guestName || '').toLowerCase();
            break;
          case 'email':
            aValue = (a.user?.email || a.guestEmail || '').toLowerCase();
            bValue = (b.user?.email || b.guestEmail || '').toLowerCase();
            break;
          case 'status':
            aValue = a.status.toLowerCase();
            bValue = b.status.toLowerCase();
            break;
          default:
            // Custom field
            let aFieldValue = '';
            let bFieldValue = '';
            if (a.bookingFields) {
              if (a.bookingFields instanceof Map) {
                aFieldValue = a.bookingFields.get(activeColumnSort) || '';
              } else if (typeof a.bookingFields === 'object') {
                aFieldValue = a.bookingFields[activeColumnSort] || '';
              }
            }
            if (b.bookingFields) {
              if (b.bookingFields instanceof Map) {
                bFieldValue = b.bookingFields.get(activeColumnSort) || '';
              } else if (typeof b.bookingFields === 'object') {
                bFieldValue = b.bookingFields[activeColumnSort] || '';
              }
            }
            aValue = aFieldValue.toLowerCase();
            bValue = bFieldValue.toLowerCase();
        }

        if (sortOrder === 'asc') {
          return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
        } else {
          return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
        }
      });
    } else {
      // Fallback to global sort
      filtered.sort((a, b) => {
        let aValue, bValue;
        
        switch (sortBy) {
          case 'date':
            // Handle null slots
            if (!a.slot || !a.slot.date) return 1;
            if (!b.slot || !b.slot.date) return -1;
            aValue = new Date(a.slot.date).getTime();
            bValue = new Date(b.slot.date).getTime();
            break;
          case 'time':
            // Handle null slots
            if (!a.slot || !a.slot.startTime) return 1;
            if (!b.slot || !b.slot.startTime) return -1;
            aValue = timeToMinutes(a.slot.startTime);
            bValue = timeToMinutes(b.slot.startTime);
            break;
          case 'name':
            aValue = (a.user?.name || a.guestName || '').toLowerCase();
            bValue = (b.user?.name || b.guestName || '').toLowerCase();
            break;
          case 'email':
            aValue = (a.user?.email || a.guestEmail || '').toLowerCase();
            bValue = (b.user?.email || b.guestEmail || '').toLowerCase();
            break;
          case 'status':
            aValue = a.status.toLowerCase();
            bValue = b.status.toLowerCase();
            break;
          default:
            aValue = new Date(a.createdAt).getTime();
            bValue = new Date(b.createdAt).getTime();
        }

        if (sortOrder === 'asc') {
          return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
        } else {
          return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
        }
      });
    }

    return filtered;
  };

  const filteredBookings = getFilteredAndSortedBookings();
  const totalPages = Math.ceil(filteredBookings.length / itemsPerPage);
  const paginatedBookings = filteredBookings.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  // Handle column-specific sort
  const handleColumnSort = (columnKey) => {
    const currentSort = columnSorts[columnKey];
    const newSorts = { ...columnSorts };
    
    // Clear other column sorts
    Object.keys(newSorts).forEach(key => {
      if (key !== columnKey) {
        delete newSorts[key];
      }
    });
    
    if (currentSort === 'asc') {
      newSorts[columnKey] = 'desc';
    } else if (currentSort === 'desc') {
      delete newSorts[columnKey];
    } else {
      newSorts[columnKey] = 'asc';
    }
    
    setColumnSorts(newSorts);
    
    // Save to config
    if (adminConfig) {
      const config = {
        ...adminConfig.bookingsTableConfig,
        columnSorts: newSorts
      };
      saveAdminConfig(config);
    }
  };

  // Handle column filter change
  const handleColumnFilterChange = (columnKey, value) => {
    const newFilters = { ...columnFilters };
    if (value && value.trim() !== '') {
      newFilters[columnKey] = value;
    } else {
      delete newFilters[columnKey];
    }
    setColumnFilters(newFilters);
    
    // Save to config
    if (adminConfig) {
      const config = {
        ...adminConfig.bookingsTableConfig,
        columnFilters: newFilters
      };
      saveAdminConfig(config);
    }
  };

  // Handle column visibility toggle
  const handleColumnVisibilityToggle = (columnKey) => {
    if (!adminConfig) return;
    
    const visibleColumns = [...(adminConfig.bookingsTableConfig?.visibleColumns || [])];
    const index = visibleColumns.indexOf(columnKey);
    
    if (index > -1) {
      visibleColumns.splice(index, 1);
    } else {
      visibleColumns.push(columnKey);
    }
    
    const config = {
      ...adminConfig.bookingsTableConfig,
      visibleColumns,
      columnOrder: visibleColumns // Update order to match visible
    };
    
    saveAdminConfig(config);
  };

  // Filter and sort slots
  const getFilteredAndSortedSlots = () => {
    let filtered = [...slots];

    // Search filter
    if (slotsSearchTerm) {
      const search = slotsSearchTerm.toLowerCase();
      filtered = filtered.filter(slot => {
        const date = formatDate(slot.date).toLowerCase();
        const time = `${slot.startTime} - ${slot.endTime}`.toLowerCase();
        const status = (slot.isAvailable ? 'available' : 'booked').toLowerCase();
        return date.includes(search) || time.includes(search) || status.includes(search);
      });
    }

    // Status filter
    if (slotsStatusFilter !== 'all') {
      filtered = filtered.filter(slot => {
        if (slotsStatusFilter === 'available') return slot.isAvailable === true;
        if (slotsStatusFilter === 'booked') return slot.isAvailable === false;
        return true;
      });
    }

    // Sort
    filtered.sort((a, b) => {
      let aValue, bValue;
      
      switch (slotsSortBy) {
        case 'date':
          aValue = new Date(a.date).getTime();
          bValue = new Date(b.date).getTime();
          break;
        case 'time':
          aValue = a.startTime;
          bValue = b.startTime;
          break;
        case 'status':
          aValue = a.isAvailable ? 'available' : 'booked';
          bValue = b.isAvailable ? 'available' : 'booked';
          break;
        default:
          aValue = new Date(a.createdAt || a.date).getTime();
          bValue = new Date(b.createdAt || b.date).getTime();
      }

      if (slotsSortOrder === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });

    return filtered;
  };

  const filteredSlots = getFilteredAndSortedSlots();
  const slotsTotalPages = Math.ceil(filteredSlots.length / slotsItemsPerPage);
  const paginatedSlots = filteredSlots.slice(
    (slotsCurrentPage - 1) * slotsItemsPerPage,
    slotsCurrentPage * slotsItemsPerPage
  );

  const handleSlotsSort = (field) => {
    if (slotsSortBy === field) {
      setSlotsSortOrder(slotsSortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSlotsSortBy(field);
      setSlotsSortOrder('asc');
    }
  };
  const [fields, setFields] = useState([]);
  
  // Slot management state
  const [slotDate, setSlotDate] = useState('');
  const [slotStartTime, setSlotStartTime] = useState('');
  const [slotEndTime, setSlotEndTime] = useState('');
  const [bulkTimeSlots, setBulkTimeSlots] = useState([{ startTime: '', endTime: '' }]);
  
  // Recurring slots state
  const [recurringStartDate, setRecurringStartDate] = useState('');
  const [recurringEndDate, setRecurringEndDate] = useState('');
  const [recurringStartTime, setRecurringStartTime] = useState('');
  const [recurringEndTime, setRecurringEndTime] = useState('');
  const [selectedDays, setSelectedDays] = useState([]);
  const [slotDuration, setSlotDuration] = useState(30); // Default 30 minutes
  
  // Field management state
  const [fieldForm, setFieldForm] = useState({
    name: '',
    label: '',
    type: 'text',
    placeholder: '',
    options: '',
    required: false,
    description: ''
  });
  const [editingField, setEditingField] = useState(null);

  // Fetch admin config
  const fetchAdminConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/admin/config`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setAdminConfig(response.data);
      setSiteAccessForm(normalizeSiteAccessConfig(response.data?.siteAccessConfig));
      setFooterForm(normalizeFooterConfig(response.data?.footerConfig));
      
      // Initialize visible columns
      if (response.data?.bookingsTableConfig?.visibleColumns) {
        // Config will be used in rendering
      }
      
      // Initialize column filters and sorts
      if (response.data?.bookingsTableConfig?.columnFilters) {
        const filters = response.data.bookingsTableConfig.columnFilters;
        if (filters instanceof Map) {
          const filterObj = {};
          filters.forEach((value, key) => {
            filterObj[key] = value;
          });
          setColumnFilters(filterObj);
        } else {
          setColumnFilters(filters || {});
        }
      }
      
      if (response.data?.bookingsTableConfig?.columnSorts) {
        const sorts = response.data.bookingsTableConfig.columnSorts;
        if (sorts instanceof Map) {
          const sortObj = {};
          sorts.forEach((value, key) => {
            sortObj[key] = value;
          });
          setColumnSorts(sortObj);
        } else {
          setColumnSorts(sorts || {});
        }
      }
    } catch (error) {
      console.error('Error fetching admin config:', error);
    }
  };

  // Save admin config
  const saveAdminConfig = async (config) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(`${API_URL}/admin/config`, {
        bookingsTableConfig: config
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setAdminConfig(response.data);
      showToast(t('admin.configSaved') || 'Configuration saved successfully', 'success');
    } catch (error) {
      console.error('Error saving admin config:', error);
      showToast(t('admin.configSaveError') || 'Failed to save configuration', 'error');
    }
  };

  const updateHours = (key, lang, value) => setFooterForm(prev => ({
    ...prev,
    workingHours: {
      ...prev.workingHours,
      [key]: { ...prev.workingHours[key], [lang]: value }
    }
  }));

  const updateContact = (key, value) => setFooterForm(prev => ({
    ...prev,
    contact: { ...prev.contact, [key]: value }
  }));

  const updateLocation = (key, value) => setFooterForm(prev => ({
    ...prev,
    location: { ...prev.location, [key]: value }
  }));

  const updateLocationAddress = (lang, value) => setFooterForm(prev => ({
    ...prev,
    location: {
      ...prev.location,
      address: { ...prev.location.address, [lang]: value }
    }
  }));

  const saveFooterConfig = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(`${API_URL}/admin/config/footer`, {
        footerConfig: normalizeFooterConfig(footerForm)
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setFooterForm(normalizeFooterConfig(response.data?.footerConfig));
      showToast(t('admin.footerSaved'), 'success');
    } catch (error) {
      console.error('Error saving footer config:', error);
      showToast(t('admin.footerSaveError'), 'error');
    }
  };

  const resetFooterConfig = () => {
    setFooterForm(defaultFooterConfig);
  };

  const saveSiteAccessConfig = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const requestedConfig = normalizeSiteAccessConfig(siteAccessForm);
      let response;
      try {
        response = await axios.put(`${API_URL}/admin/config/site-access`, {
          siteAccessConfig: requestedConfig
        }, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
      } catch (error) {
        if (error.response?.status !== 404) {
          throw error;
        }
        response = await axios.put(`${API_URL}/admin/config`, {
          siteAccessConfig: requestedConfig
        }, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
      }

      const verifyResponse = await axios.get(`${API_URL}/admin/config`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const savedConfig = normalizeSiteAccessConfig(verifyResponse.data?.siteAccessConfig || response.data?.siteAccessConfig);

      setAdminConfig(verifyResponse.data);
      setSiteAccessForm(savedConfig);

      if (savedConfig.enabled !== requestedConfig.enabled || savedConfig.mode !== requestedConfig.mode) {
        showToast(t('admin.siteAccessVerifyError'), 'error');
        return;
      }

      showToast(t('admin.siteAccessSaved'), 'success');
    } catch (error) {
      console.error('Error saving site access config:', error);
      showToast(t('admin.siteAccessSaveError'), 'error');
    }
  };

  useEffect(() => {
    if (activeTab === 'slots') fetchSlots();
    if (activeTab === 'bookings') {
      fetchBookings();
      fetchAdminConfig(); // Fetch config when viewing bookings
    }
    if (activeTab === 'fields') {
      fetchFields();
      // Don't reset form when switching to fields tab if we're editing
      if (!editingField) {
        setFieldForm({
          name: '',
          label: '',
          type: 'text',
          placeholder: '',
          options: '',
          required: false,
          description: ''
        });
      }
    }
    if (activeTab === 'siteAccess') fetchAdminConfig();
    if (activeTab === 'content') fetchAdminConfig();
    // A non-super must never sit on a gated tab.
    if (!isSuper && ['content', 'users'].includes(activeTab)) setActiveTab('slots');
  }, [activeTab]);

  // Sync form when editingField changes
  useEffect(() => {
    if (editingField && editingField._id) {
      // Make sure we have a fresh copy of the field data
      const field = { ...editingField }; // Create a copy to avoid reference issues
      const formData = {
        name: String(field.name || ''),
        label: String(field.label || ''),
        type: String(field.type || 'text'),
        placeholder: String(field.placeholder || ''),
        options: Array.isArray(field.options) && field.options.length > 0 
          ? field.options.join(', ') 
          : (field.options ? String(field.options) : ''),
        required: Boolean(field.required),
        description: String(field.description || '')
      };
      setFieldForm(formData);
    } else if (!editingField) {
      // Reset form when not editing
      setFieldForm({
        name: '',
        label: '',
        type: 'text',
        placeholder: '',
        options: '',
        required: false,
        description: ''
      });
    }
  }, [editingField]);

  const fetchSlots = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/slots`);
      setSlots(response.data);
    } catch (error) {
      console.error('Error fetching slots:', error);
    }
  };

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/bookings`);
      setBookings(response.data);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    }
  };

  const handleExcelImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const fileExtension = file.name.split('.').pop().toLowerCase();
      const isTextFile = fileExtension === 'txt' || fileExtension === 'csv';
      
      // Read file
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          let jsonData = [];
          
          if (isTextFile) {
            // Parse text/CSV file
            const text = e.target.result;
            const lines = text.split(/\r?\n/).filter(line => line.trim()); // Split by newlines and remove empty lines
            
            if (lines.length === 0) {
              showToast('Text file is empty', 'error');
              return;
            }
            
            // Detect delimiter (comma, tab, or semicolon)
            const firstLine = lines[0];
            let delimiter = ',';
            if (firstLine.includes('\t')) {
              delimiter = '\t';
            } else if (firstLine.includes(';')) {
              delimiter = ';';
            } else if (firstLine.includes(',')) {
              delimiter = ',';
            }
            
            // Parse header
            const headers = firstLine.split(delimiter).map(h => h.trim().replace(/^"|"$/g, ''));
            
            // Parse data rows
            jsonData = lines.slice(1).map((line, index) => {
              const values = line.split(delimiter).map(v => v.trim().replace(/^"|"$/g, ''));
              const row = {};
              headers.forEach((header, i) => {
                row[header] = values[i] || '';
              });
              return row;
            }).filter(row => Object.keys(row).length > 0 && Object.values(row).some(v => v !== ''));
          } else {
            // Parse Excel file
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: 'array' });
            
            // Get first sheet
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];
            
            // Convert to JSON
            jsonData = XLSX.utils.sheet_to_json(worksheet);
          }
          
          if (jsonData.length === 0) {
            showToast('File is empty', 'error');
            return;
          }

          // Helper function to convert Excel date serial number to date string (date only, no time)
          const convertExcelDate = (value) => {
            // If it's already a string, try to parse it
            if (typeof value === 'string') {
              // Try to parse date formats: "12/07/2025" or "07/12/2025" (MM/DD/YYYY or DD/MM/YYYY)
              let match = value.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
              if (match) {
                const [, part1, part2, year] = match;
                // Determine if it's MM/DD or DD/MM by checking if first part > 12
                let month, day;
                if (parseInt(part1) > 12) {
                  // DD/MM/YYYY format (e.g., 07/12/2025 = 7th December)
                  day = part1;
                  month = part2;
                } else if (parseInt(part2) > 12) {
                  // MM/DD/YYYY format (e.g., 12/07/2025 = December 7th)
                  month = part1;
                  day = part2;
                } else {
                  // Ambiguous - assume DD/MM/YYYY (European format) since user's data shows this pattern
                  day = part1;
                  month = part2;
                }
                return `${month.padStart(2, '0')}/${day.padStart(2, '0')}/${year}`;
              }
              
              // If no match, return as is
              return value;
            }
            
            // If it's a number, it's likely an Excel serial date
            if (typeof value === 'number') {
              // Excel date serial number: days since Jan 1, 1900
              const excelEpoch = new Date(1900, 0, 1);
              const days = Math.floor(value);
              
              // Excel incorrectly treats 1900 as a leap year, so we need to adjust
              const date = new Date(excelEpoch);
              date.setDate(date.getDate() + days - 2); // -2 because Excel counts from Jan 1, 1900 incorrectly
              
              // Format as MM/DD/YYYY (date only, no time)
              const month = String(date.getMonth() + 1).padStart(2, '0');
              const day = String(date.getDate()).padStart(2, '0');
              const year = date.getFullYear();
              
              return `${month}/${day}/${year}`;
            }
            
            return String(value);
          };

          // Map Excel columns to booking data
          // Expected columns: name, booking_date, booking_time (or combined booking), and other custom fields
          const bookings = jsonData.map((row, index) => {
            const bookingData = {};
            
            // Find booking date column (case-insensitive)
            const bookingDateKey = Object.keys(row).find(key => 
              key.toLowerCase().includes('booking_date') || 
              key.toLowerCase() === 'date' ||
              (key.toLowerCase().includes('booking') && key.toLowerCase().includes('date'))
            );
            
            // Find booking time column (case-insensitive)
            const bookingTimeKey = Object.keys(row).find(key => 
              key.toLowerCase().includes('booking_time') || 
              (key.toLowerCase() === 'time' && !key.toLowerCase().includes('date')) ||
              (key.toLowerCase().includes('booking') && key.toLowerCase().includes('time'))
            );
            
            // If we have separate date and time columns
            if (bookingDateKey && bookingTimeKey) {
              const dateValue = convertExcelDate(row[bookingDateKey]);
              const timeValue = row[bookingTimeKey];
              
              // Combine date and time
              bookingData.bookingDateTime = `${dateValue} ${timeValue}`;
            } else {
              // Try to find combined booking column (backward compatibility)
              const bookingKey = Object.keys(row).find(key => 
                key.toLowerCase().includes('booking') && 
                !key.toLowerCase().includes('date') &&
                !key.toLowerCase().includes('time')
              );
              
              if (bookingKey) {
                // Convert Excel date to proper format
                bookingData.bookingDateTime = convertExcelDate(row[bookingKey]);
              } else {
                throw new Error(`Row ${index + 2}: No booking date/time columns found. Please ensure your Excel has "booking_date" and "booking_time" columns, or a combined "booking" column.`);
              }
            }
            
            // Find name column (case-insensitive)
            const nameKey = Object.keys(row).find(key => 
              key.toLowerCase() === 'name' || 
              key.toLowerCase().includes('اسم') ||
              key.toLowerCase().includes('patient')
            );
            
            if (nameKey) {
              bookingData.name = row[nameKey];
            }

            // Add all other columns as booking fields (exclude booking date/time columns)
            Object.keys(row).forEach(key => {
              if (key !== bookingDateKey && key !== bookingTimeKey && key !== nameKey) {
                // Also exclude if it's a combined booking column
                const isCombinedBooking = key.toLowerCase().includes('booking') && 
                  !key.toLowerCase().includes('date') && 
                  !key.toLowerCase().includes('time');
                if (!isCombinedBooking) {
                  bookingData[key] = row[key] !== null && row[key] !== undefined ? String(row[key]) : '';
                }
              }
            });

            return bookingData;
          });

          // Send to backend for validation and import
          const token = localStorage.getItem('token');
          try {
            const response = await axios.post(
              `${API_URL}/admin/bookings/import`,
              { bookings },
              {
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                }
              }
            );

            // Success - all passed
            const { passed, failed, total, results } = response.data;
            showToast(`Successfully imported ${passed} booking(s)`, 'success');
            fetchBookings();
            fetchSlots(); // Refresh slots too
            
            // Show modal with all results
            setImportResults({
              passed,
              failed,
              total,
              results
            });
          } catch (error) {
            console.error('Error processing Excel:', error);
            
            // Check if there are failures (400 status with results)
            if (error.response?.status === 400 && error.response?.data?.results) {
              const { passed, failed, total, results } = error.response.data;
              
              // Show toast with failure count
              showToast(`${failed} out of ${total} record(s) failed. Please check the details.`, 'error');
              
              // Show modal with all results
              setImportResults({
                passed,
                failed,
                total,
                results
              });
            } else {
              showToast(error.response?.data?.message || error.message || 'Failed to import bookings', 'error');
            }
          }
          
          // Reset file input
          event.target.value = '';
        } catch (error) {
          console.error('Error processing file:', error);
          showToast(error.message || 'Failed to read file', 'error');
          event.target.value = '';
        }
      };
      
      // Read file based on type (fileExtension and isTextFile already declared above)
      if (isTextFile) {
        reader.readAsText(file);
      } else {
        reader.readAsArrayBuffer(file);
      }
    } catch (error) {
      console.error('Error reading file:', error);
      showToast('Failed to read file', 'error');
      event.target.value = '';
    }
  };

  const handleUnbook = async () => {
    if (!unbookBooking) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/admin/bookings/${unbookBooking._id}/unbook`, {}, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      showToast(t('admin.unbookSuccess'), 'success');
      setUnbookBooking(null);
      fetchBookings();
      fetchSlots(); // Refresh slots to show the unbooked slot as available
    } catch (error) {
      showToast(error.response?.data?.message || t('admin.unbookError'), 'error');
    }
  };

  const [selectedSlotForEdit, setSelectedSlotForEdit] = useState(null);

  const handleEditBooking = (booking) => {
    if (!booking.slot || !booking.slot.date) {
      showToast('Cannot edit booking: slot information is missing', 'error');
      return;
    }
    setEditingBooking(booking);
    // Set the current booking date
    const bookingDate = new Date(booking.slot.date).toISOString().split('T')[0];
    setEditBookingDate(bookingDate);
    // Set current slot as selected
    setSelectedSlotForEdit(booking.slot._id);
    // Fetch slots for that date
    fetchSlotsForEdit(bookingDate);
  };

  const fetchSlotsForEdit = async (date) => {
    if (!date) return;
    try {
      const response = await axios.get(`${API_URL}/bookings/slots`, {
        params: { date }
      });
      setEditBookingSlots(response.data || []);
    } catch (error) {
      console.error('Error fetching slots for edit:', error);
      setEditBookingSlots([]);
    }
  };

  const handleEditBookingDateChange = (newDate) => {
    setEditBookingDate(newDate);
    setSelectedSlotForEdit(null); // Reset selection when date changes
    fetchSlotsForEdit(newDate);
  };

  const handleSaveBookingEdit = async () => {
    if (!editingBooking || !selectedSlotForEdit) {
      showToast(isRTL ? 'يرجى اختيار موعد' : 'Please select a slot', 'error');
      return;
    }

    // If same slot, just close
    if (selectedSlotForEdit === editingBooking.slot._id) {
      setEditingBooking(null);
      setEditBookingDate('');
      setEditBookingSlots([]);
      setSelectedSlotForEdit(null);
      return;
    }

    setEditBookingLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/admin/bookings/${editingBooking._id}`,
        { slotId: selectedSlotForEdit },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      showToast(t('admin.bookingUpdateSuccess') || (isRTL ? 'تم تحديث الحجز بنجاح' : 'Booking updated successfully'), 'success');
      setEditingBooking(null);
      setEditBookingDate('');
      setEditBookingSlots([]);
      setSelectedSlotForEdit(null);
      fetchBookings();
      fetchSlots(); // Refresh slots
    } catch (error) {
      showToast(error.response?.data?.message || t('admin.bookingUpdateError') || (isRTL ? 'فشل تحديث الحجز' : 'Failed to update booking'), 'error');
    } finally {
      setEditBookingLoading(false);
    }
  };

  const fetchFields = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/fields`);
      setFields(response.data);
    } catch (error) {
      console.error('Error fetching fields:', error);
    }
  };

  const handleCreateSlot = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/admin/slots`, {
        date: slotDate,
        startTime: slotStartTime,
        endTime: slotEndTime
      });
      showToast('Slot created successfully', 'success');
      setSlotDate('');
      setSlotStartTime('');
      setSlotEndTime('');
      fetchSlots();
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to create slot', 'error');
    }
  };

  const handleBulkCreateSlots = async (e) => {
    e.preventDefault();
    const validSlots = bulkTimeSlots.filter(s => s.startTime && s.endTime);
    if (validSlots.length === 0) {
      showToast('Please add at least one time slot', 'warning');
      return;
    }
    try {
      await axios.post(`${API_URL}/admin/slots/bulk`, {
        date: slotDate,
        timeSlots: validSlots
      });
      showToast(`Created ${validSlots.length} slots successfully`, 'success');
      setSlotDate('');
      setBulkTimeSlots([{ startTime: '', endTime: '' }]);
      fetchSlots();
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to create slots', 'error');
    }
  };

  const handleDeleteSlot = async (id) => {
    if (window.confirm('Are you sure you want to delete this slot?')) {
      try {
        await axios.delete(`${API_URL}/admin/slots/${id}`);
        showToast('Slot deleted successfully', 'success');
        fetchSlots();
      } catch (error) {
        showToast(error.response?.data?.message || 'Failed to delete slot', 'error');
      }
    }
  };

  const handleAddTimeSlot = () => {
    setBulkTimeSlots([...bulkTimeSlots, { startTime: '', endTime: '' }]);
  };

  const handleRemoveTimeSlot = (index) => {
    setBulkTimeSlots(bulkTimeSlots.filter((_, i) => i !== index));
  };

  const handleTimeSlotChange = (index, field, value) => {
    const updated = [...bulkTimeSlots];
    updated[index][field] = value;
    setBulkTimeSlots(updated);
  };

  const handleRecurringSlotCreate = async (e) => {
    e.preventDefault();
    if (selectedDays.length === 0) {
      showToast(t('admin.selectAtLeastOneDay'), 'error');
      return;
    }

    if (!recurringStartTime || !recurringEndTime) {
      showToast(t('admin.startTimeAndEndTimeRequired'), 'error');
      return;
    }

    // Calculate number of slots that will be created
    const startMinutes = timeToMinutes(recurringStartTime);
    const endMinutes = timeToMinutes(recurringEndTime);
    const slotsPerDay = Math.floor((endMinutes - startMinutes) / slotDuration);
    const totalDays = calculateDaysBetween(recurringStartDate, recurringEndDate, selectedDays);
    const totalSlots = slotsPerDay * totalDays;

    if (totalSlots > 1000) {
      if (!window.confirm(t('admin.largeNumberOfSlots', { count: totalSlots }))) {
        return;
      }
    }

    try {
      const response = await axios.post(`${API_URL}/admin/slots/recurring`, {
        startDate: recurringStartDate,
        endDate: recurringEndDate,
        daysOfWeek: selectedDays,
        startTime: recurringStartTime,
        endTime: recurringEndTime,
        slotDuration: slotDuration
      });
      showToast(t('admin.recurringSlotsCreated', { count: response.data.slots.length }), 'success');
      setRecurringStartDate('');
      setRecurringEndDate('');
      setRecurringStartTime('');
      setRecurringEndTime('');
      setSelectedDays([]);
      setSlotDuration(30); // Reset to default
      fetchSlots();
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to create recurring slots', 'error');
    }
  };

  const toggleDay = (day) => {
    setSelectedDays(prev => 
      prev.includes(day) 
        ? prev.filter(d => d !== day)
        : [...prev, day]
    );
  };

  const daysOfWeek = [
    { value: 0, label: t('admin.sunday'), short: 'Sun' },
    { value: 1, label: t('admin.monday'), short: 'Mon' },
    { value: 2, label: t('admin.tuesday'), short: 'Tue' },
    { value: 3, label: t('admin.wednesday'), short: 'Wed' },
    { value: 4, label: t('admin.thursday'), short: 'Thu' },
    { value: 5, label: t('admin.friday'), short: 'Fri' },
    { value: 6, label: t('admin.saturday'), short: 'Sat' }
  ];

  const handleCreateField = async (e) => {
    e.preventDefault();
    try {
      const fieldData = {
        ...fieldForm,
        options: fieldForm.options ? fieldForm.options.split(',').map(o => o.trim()) : []
      };
      await axios.post(`${API_URL}/admin/fields`, fieldData);
      showToast('Field created successfully', 'success');
      setFieldForm({
        name: '',
        label: '',
        type: 'text',
        placeholder: '',
        options: '',
        required: false,
        description: ''
      });
      fetchFields();
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to create field', 'error');
    }
  };

  const handleUpdateField = async (e) => {
    e.preventDefault();
    try {
      const fieldData = {
        ...fieldForm,
        options: fieldForm.options ? fieldForm.options.split(',').map(o => o.trim()).filter(o => o) : []
      };
      await axios.put(`${API_URL}/admin/fields/${editingField._id}`, fieldData);
      showToast('Field updated successfully', 'success');
      setEditingField(null);
      // Form will be reset by useEffect when editingField becomes null
      fetchFields();
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to update field', 'error');
    }
  };

  const handleEditField = (field) => {
    console.log('handleEditField called with:', field);
    // Immediately populate the form
    const formData = {
      name: String(field.name || ''),
      label: String(field.label || ''),
      type: String(field.type || 'text'),
      placeholder: String(field.placeholder || ''),
      options: Array.isArray(field.options) && field.options.length > 0 
        ? field.options.join(', ') 
        : (field.options ? String(field.options) : ''),
      required: Boolean(field.required),
      description: String(field.description || '')
    };
    console.log('Setting form data immediately:', formData);
    setFieldForm(formData);
    setEditingField(field);
  };

  const handleDeleteField = async (id) => {
    if (window.confirm('Are you sure you want to delete this field?')) {
      try {
        await axios.delete(`${API_URL}/admin/fields/${id}`);
        showToast('Field deleted successfully', 'success');
        fetchFields();
      } catch (error) {
        showToast(error.response?.data?.message || 'Failed to delete field', 'error');
      }
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8 py-4 sm:py-6 lg:py-8" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="mb-4 sm:mb-6 lg:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1 sm:mb-2">{t('admin.title')}</h1>
        <p className="text-sm sm:text-base text-gray-600">{t('admin.subtitle')}</p>
      </div>

      {/* Tabs */}
      <div className="mb-4 sm:mb-6 border-b border-gray-200 overflow-x-auto" dir={isRTL ? 'rtl' : 'ltr'}>
        <nav className={`-mb-px flex ${isRTL ? 'flex-row-reverse justify-end' : 'justify-start'} gap-0`}>
          <button
            onClick={() => setActiveTab('slots')}
            className={`py-3 sm:py-4 px-4 sm:px-6 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap transition-colors ${
              activeTab === 'slots'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {t('admin.manageSlots')}
          </button>
          <button
            onClick={() => setActiveTab('fields')}
            className={`py-3 sm:py-4 px-4 sm:px-6 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap transition-colors ${
              activeTab === 'fields'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {t('admin.bookingFields')}
          </button>
          <button
            onClick={() => setActiveTab('bookings')}
            className={`py-3 sm:py-4 px-4 sm:px-6 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap transition-colors ${
              activeTab === 'bookings'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {t('admin.viewBookings')} ({bookings.length})
          </button>
          <button
            onClick={() => setActiveTab('siteAccess')}
            className={`py-3 sm:py-4 px-4 sm:px-6 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap transition-colors ${
              activeTab === 'siteAccess'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {t('admin.siteAccess')}
          </button>
          {isSuper && (
            <button
              onClick={() => setActiveTab('content')}
              className={`py-3 sm:py-4 px-4 sm:px-6 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap transition-colors ${
                activeTab === 'content'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {t('admin.content')}
            </button>
          )}
          {isSuper && (
            <button
              onClick={() => setActiveTab('users')}
              className={`py-3 sm:py-4 px-4 sm:px-6 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap transition-colors ${
                activeTab === 'users'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {t('admin.users')}
            </button>
          )}
          <button
            onClick={() => setActiveTab('pricing')}
            className={`py-3 sm:py-4 px-4 sm:px-6 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap transition-colors ${
              activeTab === 'pricing'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {t('admin.pricing')}
          </button>
        </nav>
      </div>

      {/* Site Access Tab */}
      {activeTab === 'siteAccess' && (
        <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
          <div className="mb-5">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900">{t('admin.siteAccess')}</h2>
            <p className="text-sm text-gray-600 mt-1">{t('admin.siteAccessDescription')}</p>
          </div>
          <form onSubmit={saveSiteAccessConfig} className="space-y-6">
            <label className="flex items-start gap-3 rounded-lg border border-gray-200 p-4 cursor-pointer hover:border-blue-300">
              <input
                type="checkbox"
                checked={siteAccessForm.enabled}
                onChange={(e) => setSiteAccessForm(prev => ({ ...prev, enabled: e.target.checked }))}
                className="mt-1 h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span>
                <span className="block text-sm font-semibold text-gray-900">{t('admin.blockSiteAccess')}</span>
                <span className="block text-sm text-gray-600 mt-1">{t('admin.blockSiteAccessHint')}</span>
              </span>
            </label>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('admin.blockReason')}</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {['maintenance', 'vacation'].map((mode) => (
                  <label
                    key={mode}
                    className={`rounded-lg border p-4 cursor-pointer transition ${
                      siteAccessForm.mode === mode
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="siteAccessMode"
                      value={mode}
                      checked={siteAccessForm.mode === mode}
                      onChange={(e) => setSiteAccessForm(prev => ({ ...prev, mode: e.target.value }))}
                      className="sr-only"
                    />
                    <span className="block text-sm font-semibold text-gray-900">
                      {t(`admin.siteAccess.${mode}`)}
                    </span>
                    <span className="block text-sm text-gray-600 mt-1">
                      {siteAccessForm.messages?.[mode]?.[language]}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {['maintenance', 'vacation'].map((mode) => (
                <div key={mode} className="space-y-3">
                  <h3 className="text-sm font-semibold text-gray-900">{t(`admin.siteAccess.${mode}`)}</h3>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.englishMessage')}</label>
                    <textarea
                      value={siteAccessForm.messages?.[mode]?.en || ''}
                      onChange={(e) => setSiteAccessForm(prev => ({
                        ...prev,
                        messages: {
                          ...prev.messages,
                          [mode]: {
                            ...prev.messages[mode],
                            en: e.target.value
                          }
                        }
                      }))}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      dir="ltr"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.arabicMessage')}</label>
                    <textarea
                      value={siteAccessForm.messages?.[mode]?.ar || ''}
                      onChange={(e) => setSiteAccessForm(prev => ({
                        ...prev,
                        messages: {
                          ...prev.messages,
                          [mode]: {
                            ...prev.messages[mode],
                            ar: e.target.value
                          }
                        }
                      }))}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      dir="rtl"
                    />
                  </div>
                </div>
              ))}
            </div>

            <button
              type="submit"
              className="w-full sm:w-auto px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
            >
              {t('admin.saveSiteAccess')}
            </button>
          </form>

        </div>
      )}

      {/* Content / CMS Tab */}
      {activeTab === 'content' && isSuper && (
        <form onSubmit={saveFooterConfig} className="space-y-4">
          <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900">{t('admin.content')}</h2>
            <p className="text-sm text-gray-600 mt-1">{t('admin.contentDescription')}</p>
          </div>

          {/* Working hours */}
          <CmsSection
            id="hours"
            title={t('admin.footerHours')}
            description={t('admin.footerHoursDescription')}
            open={openCmsSection === 'hours'}
            onToggle={setOpenCmsSection}
            isRTL={isRTL}
          >
            {[
              { key: 'title', label: t('admin.footerHoursTitle') },
              { key: 'days', label: t('admin.footerHoursDays') },
              { key: 'time', label: t('admin.footerHoursTime') }
            ].map(({ key, label }) => (
              <div key={key} className="border border-gray-200 rounded-lg p-3 sm:p-4 space-y-3">
                <h4 className="text-sm font-semibold text-gray-900">{label}</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">English</label>
                    <input
                      type="text"
                      value={footerForm.workingHours?.[key]?.en || ''}
                      onChange={(e) => updateHours(key, 'en', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      dir="ltr"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">العربية</label>
                    <input
                      type="text"
                      value={footerForm.workingHours?.[key]?.ar || ''}
                      onChange={(e) => updateHours(key, 'ar', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      dir="rtl"
                    />
                  </div>
                </div>
              </div>
            ))}

            <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 sm:p-4">
              <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-2">
                {t('admin.footerPreview')}
              </p>
              <div className="text-sm text-gray-700 space-y-1" dir={isRTL ? 'rtl' : 'ltr'}>
                <p className="font-semibold">{footerForm.workingHours?.title?.[language]}</p>
                <p>{footerForm.workingHours?.days?.[language]}</p>
                <p>{footerForm.workingHours?.time?.[language]}</p>
              </div>
            </div>
          </CmsSection>

          {/* Contact + social accounts */}
          <CmsSection
            id="contact"
            title={t('admin.contactAccounts')}
            description={t('admin.contactAccountsDescription')}
            open={openCmsSection === 'contact'}
            onToggle={setOpenCmsSection}
            isRTL={isRTL}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              {[
                { key: 'phone', label: t('admin.contactPhone'), hint: '966500000000', icon: 'phone' },
                { key: 'phoneDisplay', label: t('admin.contactPhoneDisplay'), hint: '+966 50 000 0000', icon: 'phone' },
                { key: 'whatsapp', label: 'WhatsApp', hint: '966500000000', icon: 'whatsapp' },
                { key: 'email', label: t('admin.contactEmail'), hint: 'clinic@example.com', icon: 'email' },
                { key: 'instagram', label: 'Instagram', hint: '@clinic', icon: 'instagram' },
                { key: 'snapchat', label: 'Snapchat', hint: '@clinic', icon: 'snapchat' },
                { key: 'tiktok', label: 'TikTok', hint: '@clinic', icon: 'tiktok' },
                { key: 'twitter', label: 'X (Twitter)', hint: '@clinic', icon: 'twitter' }
              ].map(({ key, label, hint }) => (
                <div key={key}>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">{label}</label>
                  <input
                    type="text"
                    value={footerForm.contact?.[key] || ''}
                    onChange={(e) => updateContact(key, e.target.value)}
                    placeholder={hint}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    dir="ltr"
                  />
                </div>
              ))}
            </div>
            <p className="text-xs text-gray-500">{t('admin.contactHint')}</p>
          </CmsSection>

          {/* Clinic location */}
          <CmsSection
            id="location"
            title={t('admin.clinicLocation')}
            description={t('admin.clinicLocationDescription')}
            open={openCmsSection === 'location'}
            onToggle={setOpenCmsSection}
            isRTL={isRTL}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">
                  {t('admin.addressEn')}
                </label>
                <input
                  type="text"
                  value={footerForm.location?.address?.en || ''}
                  onChange={(e) => updateLocationAddress('en', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  dir="ltr"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">
                  {t('admin.addressAr')}
                </label>
                <input
                  type="text"
                  value={footerForm.location?.address?.ar || ''}
                  onChange={(e) => updateLocationAddress('ar', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  dir="rtl"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                {t('admin.mapUrl')}
              </label>
              <input
                type="url"
                value={footerForm.location?.mapUrl || ''}
                onChange={(e) => updateLocation('mapUrl', e.target.value)}
                placeholder="https://maps.app.goo.gl/..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                dir="ltr"
              />
              <p className="text-xs text-gray-500 mt-1">{t('admin.mapUrlHint')}</p>
            </div>
          </CmsSection>

          {/* Sticky save bar */}
          <div className="sticky bottom-0 bg-white border border-gray-200 rounded-lg shadow-lg p-3 sm:p-4">
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <button
                type="submit"
                className="w-full sm:w-auto px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
              >
                {t('admin.saveFooterHours')}
              </button>
              <button
                type="button"
                onClick={resetFooterConfig}
                className="w-full sm:w-auto px-5 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
              >
                {t('admin.resetToDefault')}
              </button>
            </div>
          </div>
        </form>
      )}

      {/* Users Tab - super admin only */}
      {activeTab === 'users' && isSuper && <AdminUsers showToast={showToast} />}

      {/* Pricing Tab */}
      {activeTab === 'pricing' && <AdminPricing showToast={showToast} />}

      {/* Slots Tab */}
      {activeTab === 'slots' && (
        <div className="space-y-4 sm:space-y-6">
          {/* Create Single Slot */}
          <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
            <h2 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4">{t('admin.createSlot')}</h2>
            <form onSubmit={handleCreateSlot} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              <DatePicker
                value={slotDate}
                onChange={setSlotDate}
                min={new Date().toISOString().split('T')[0]}
                placeholder="Select Date"
                className="w-full"
              />
              <TimePicker
                value={slotStartTime}
                onChange={setSlotStartTime}
                placeholder="Start Time"
                className="w-full"
              />
              <TimePicker
                value={slotEndTime}
                onChange={setSlotEndTime}
                placeholder="End Time"
                minTime={slotStartTime}
                className="w-full"
              />
              <button
                type="submit"
                className="px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm sm:text-base"
              >
                {t('admin.createSlot')}
              </button>
            </form>
          </div>

          {/* Bulk Create Slots */}
          <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
            <h2 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4">{t('admin.bulkCreate')}</h2>
            <form onSubmit={handleBulkCreateSlots} className="space-y-3 sm:space-y-4">
              <DatePicker
                value={slotDate}
                onChange={setSlotDate}
                min={new Date().toISOString().split('T')[0]}
                placeholder="Select Date"
                className="w-full"
              />
              {bulkTimeSlots.map((slot, index) => (
                <div key={index} className="flex flex-col sm:flex-row gap-2 sm:gap-4">
                  <TimePicker
                    value={slot.startTime}
                    onChange={(value) => handleTimeSlotChange(index, 'startTime', value)}
                    placeholder="Start Time"
                    className="flex-1"
                  />
                  <TimePicker
                    value={slot.endTime}
                    onChange={(value) => handleTimeSlotChange(index, 'endTime', value)}
                    placeholder="End Time"
                    minTime={slot.startTime}
                    className="flex-1"
                  />
                  {bulkTimeSlots.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleRemoveTimeSlot(index)}
                      className="px-3 sm:px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm sm:text-base"
                    >
                      {t('admin.remove')}
                    </button>
                  )}
                </div>
              ))}
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
                <button
                  type="button"
                  onClick={handleAddTimeSlot}
                  className="px-3 sm:px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm sm:text-base"
                >
                  {t('admin.addTimeSlot')}
                </button>
                <button
                  type="submit"
                  className="px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm sm:text-base"
                >
                  {t('admin.createAllSlots')}
                </button>
              </div>
            </form>
          </div>

          {/* Recurring Daily Slots */}
          <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
            <h2 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4">{t('admin.recurringSlots')}</h2>
            <form onSubmit={handleRecurringSlotCreate} className="space-y-3 sm:space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('admin.dateRange')}</label>
                <DateRangePicker
                  startDate={recurringStartDate}
                  endDate={recurringEndDate}
                  onStartDateChange={setRecurringStartDate}
                  onEndDateChange={setRecurringEndDate}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('admin.startTime')}</label>
                  <TimePicker
                    value={recurringStartTime}
                    onChange={setRecurringStartTime}
                    placeholder={t('admin.startTime')}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('admin.endTime')}</label>
                  <TimePicker
                    value={recurringEndTime}
                    onChange={setRecurringEndTime}
                    placeholder={t('admin.endTime')}
                    minTime={recurringStartTime}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('admin.slotDuration')}</label>
                  <select
                    value={slotDuration}
                    onChange={(e) => setSlotDuration(parseInt(e.target.value))}
                    className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white hover:border-blue-400 transition-all shadow-sm"
                    dir={isRTL ? 'rtl' : 'ltr'}
                  >
                    <option value={15}>15 {t('admin.minutes')}</option>
                    <option value={30}>30 {t('admin.minutes')}</option>
                    <option value={45}>45 {t('admin.minutes')}</option>
                    <option value={60}>60 {t('admin.minutes')} (1 {t('admin.hour')})</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {t('admin.slotDurationHint')}
                  </p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('admin.selectDays')}</label>
                <div className="flex flex-wrap gap-2">
                  {daysOfWeek.map((day) => (
                    <button
                      key={day.value}
                      type="button"
                      onClick={() => toggleDay(day.value)}
                      className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg font-medium transition text-xs sm:text-sm ${
                        selectedDays.includes(day.value)
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {day.label}
                    </button>
                  ))}
                </div>
              </div>
              <button
                type="submit"
                className="w-full px-3 sm:px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 text-sm sm:text-base"
              >
                {t('admin.createRecurring')}
              </button>
            </form>
          </div>

          {/* Slots List */}
          <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
            <h2 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4">{t('admin.allSlots')}</h2>
            
            {/* Filters and Search */}
            <div className="mb-4 space-y-3 sm:space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                {/* Search */}
                <div className="sm:col-span-2">
                  <input
                    type="text"
                    placeholder={isRTL ? 'بحث...' : 'Search...'}
                    value={slotsSearchTerm}
                    onChange={(e) => {
                      setSlotsSearchTerm(e.target.value);
                      setSlotsCurrentPage(1);
                    }}
                    className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    dir={isRTL ? 'rtl' : 'ltr'}
                  />
                </div>
                {/* Status Filter */}
                <div>
                  <select
                    value={slotsStatusFilter}
                    onChange={(e) => {
                      setSlotsStatusFilter(e.target.value);
                      setSlotsCurrentPage(1);
                    }}
                    className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    dir={isRTL ? 'rtl' : 'ltr'}
                  >
                    <option value="all">{isRTL ? 'جميع الحالات' : 'All Status'}</option>
                    <option value="available">{t('admin.available')}</option>
                    <option value="booked">{t('admin.booked')}</option>
                  </select>
                </div>
                {/* Reset Filters Button */}
                <div>
                  <button
                    onClick={() => {
                      setSlotsSearchTerm('');
                      setSlotsStatusFilter('all');
                      setSlotsSortBy('date');
                      setSlotsSortOrder('desc');
                      setSlotsCurrentPage(1);
                    }}
                    className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition font-medium text-sm"
                  >
                    {isRTL ? 'إعادة تعيين' : 'Reset Filters'}
                  </button>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto -mx-4 sm:mx-0">
              <div className="inline-block min-w-full align-middle">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th
                        onClick={() => handleSlotsSort('date')}
                        className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                      >
                        {t('admin.date')}
                        {slotsSortBy === 'date' && (
                          <span className="ml-1">{slotsSortOrder === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </th>
                      <th
                        onClick={() => handleSlotsSort('time')}
                        className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                      >
                        {t('booking.time')}
                        {slotsSortBy === 'time' && (
                          <span className="ml-1">{slotsSortOrder === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </th>
                      <th
                        onClick={() => handleSlotsSort('status')}
                        className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                      >
                        {t('admin.status')}
                        {slotsSortBy === 'status' && (
                          <span className="ml-1">{slotsSortOrder === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </th>
                      <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">{t('admin.bookedBy')}</th>
                      <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('admin.actions')}</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {paginatedSlots.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-3 sm:px-6 py-8 text-center text-gray-500">
                          {t('admin.noBookings')}
                        </td>
                      </tr>
                    ) : (
                      paginatedSlots.map((slot) => (
                        <tr key={slot._id} className="hover:bg-gray-50">
                          <td className="px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm text-gray-900">
                            {formatDate(slot.date)}
                          </td>
                          <td className="px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm text-gray-900">
                            {formatTime(slot.startTime)} - {formatTime(slot.endTime)}
                          </td>
                          <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              slot.isAvailable
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {slot.isAvailable ? t('admin.available') : t('admin.booked')}
                            </span>
                          </td>
                          <td className="px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm text-gray-500 hidden sm:table-cell">
                            {slot.bookedBy ? slot.bookedBy.name : '-'}
                          </td>
                          <td className="px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm">
                            {slot.isAvailable && (
                              <button
                                onClick={() => handleDeleteSlot(slot._id)}
                                className="text-red-600 hover:text-red-900"
                              >
                                {t('delete')}
                              </button>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            {slotsTotalPages > 1 && (
              <div className="mt-4 flex flex-col sm:flex-row justify-between items-center gap-3">
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-700">
                    {isRTL ? 'عناصر لكل صفحة:' : 'Items per page:'}
                  </label>
                  <select
                    value={slotsItemsPerPage}
                    onChange={(e) => {
                      setSlotsItemsPerPage(Number(e.target.value));
                      setSlotsCurrentPage(1);
                    }}
                    className="px-3 py-1 border border-gray-300 rounded-lg"
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setSlotsCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={slotsCurrentPage === 1}
                    className="px-3 py-1 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    {isRTL ? 'السابق' : 'Previous'}
                  </button>
                  <span className="text-sm text-gray-700">
                    {isRTL ? `صفحة ${slotsCurrentPage} من ${slotsTotalPages}` : `Page ${slotsCurrentPage} of ${slotsTotalPages}`}
                  </span>
                  <button
                    onClick={() => setSlotsCurrentPage(prev => Math.min(slotsTotalPages, prev + 1))}
                    disabled={slotsCurrentPage === slotsTotalPages}
                    className="px-3 py-1 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    {isRTL ? 'التالي' : 'Next'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Fields Tab */}
      {activeTab === 'fields' && (
        <div className="space-y-4 sm:space-y-6">
          {/* Create/Edit Field Form */}
          <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
            <h2 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4">
              {editingField ? t('admin.editField') : t('admin.createField')}
            </h2>
            <form onSubmit={editingField ? handleUpdateField : handleCreateField} className="space-y-3 sm:space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.fieldName')}</label>
                  <input
                    type="text"
                    value={fieldForm.name}
                    onChange={(e) => setFieldForm({ ...fieldForm, name: e.target.value })}
                    required
                    disabled={!!editingField}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.fieldLabel')}</label>
                  <input
                    type="text"
                    value={fieldForm.label}
                    onChange={(e) => setFieldForm({ ...fieldForm, label: e.target.value })}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.fieldType')}</label>
                  <select
                    value={fieldForm.type}
                    onChange={(e) => setFieldForm({ ...fieldForm, type: e.target.value })}
                    className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white hover:border-blue-400 transition-all shadow-sm"
                    dir={isRTL ? 'rtl' : 'ltr'}
                  >
                    <option value="text">Text</option>
                    <option value="email">Email</option>
                    <option value="tel">Phone</option>
                    <option value="textarea">Textarea</option>
                    <option value="select">Select</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.fieldPlaceholder')}</label>
                  <input
                    type="text"
                    value={fieldForm.placeholder}
                    onChange={(e) => setFieldForm({ ...fieldForm, placeholder: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                {fieldForm.type === 'select' && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('admin.fieldOptions')}
                    </label>
                    <input
                      type="text"
                      value={fieldForm.options}
                      onChange={(e) => setFieldForm({ ...fieldForm, options: e.target.value })}
                      placeholder="Option 1, Option 2, Option 3"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                )}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.fieldDescription')}</label>
                  <div className="border border-gray-300 rounded-lg overflow-hidden">
                    <ReactQuill
                      key={editingField?._id || 'new'}
                      theme="snow"
                      value={fieldForm.description || ''}
                      onChange={(value) => setFieldForm({ ...fieldForm, description: value })}
                      modules={{
                        toolbar: [
                          [{ 'header': [1, 2, 3, false] }],
                          ['bold', 'italic', 'underline', 'strike'],
                          [{ 'color': [] }, { 'background': [] }],
                          [{ 'size': ['small', false, 'large', 'huge'] }],
                          [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                          [{ 'align': [] }],
                          ['link'],
                          ['clean']
                        ]
                      }}
                      formats={[
                        'header', 'bold', 'italic', 'underline', 'strike',
                        'color', 'background', 'size',
                        'list', 'bullet', 'align', 'link'
                      ]}
                      style={{ minHeight: '150px' }}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {isRTL 
                      ? 'استخدم أزرار التنسيق أعلاه لتنسيق النص'
                      : 'Use the formatting buttons above to style your text'
                    }
                  </p>
                </div>
                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={fieldForm.required}
                      onChange={(e) => setFieldForm({ ...fieldForm, required: e.target.checked })}
                      className="mr-2"
                    />
                    <span className="text-sm font-medium text-gray-700">{t('admin.fieldRequired')}</span>
                  </label>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
                <button
                  type="submit"
                  className="px-4 sm:px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm sm:text-base"
                >
                  {editingField ? t('update') : t('create')}
                </button>
                {editingField && (
                  <button
                    type="button"
                    onClick={() => {
                      setEditingField(null);
                      setFieldForm({
                        name: '',
                        label: '',
                        type: 'text',
                        placeholder: '',
                        options: '',
                        required: false,
                        description: ''
                      });
                    }}
                    className="px-4 sm:px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm sm:text-base"
                  >
                    {t('cancel')}
                  </button>
                )}
              </div>
            </form>
          </div>

          {/* Fields List */}
          <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
            <h2 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4">{t('admin.bookingFields')}</h2>
            <div className="space-y-3 sm:space-y-4">
              {fields.map((field) => (
                <div key={field._id} className="border border-gray-200 rounded-lg p-3 sm:p-4">
                  <div className="flex flex-col sm:flex-row justify-between items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <h3 className="font-semibold text-gray-900 text-sm sm:text-base">{field.label}</h3>
                        <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded">
                          {field.type}
                        </span>
                        {field.required && (
                          <span className="text-xs px-2 py-1 bg-red-100 text-red-600 rounded">
                            {t('validation.required')}
                          </span>
                        )}
                      </div>
                      {field.description && (
                        <p className="text-xs sm:text-sm text-gray-600 mb-2">{field.description}</p>
                      )}
                      <p className="text-xs text-gray-500">Name: {field.name}</p>
                      {field.options && Array.isArray(field.options) && field.options.length > 0 && (
                        <p className="text-xs text-gray-500 mt-1 break-words">
                          Options: {field.options.join(', ')}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2 w-full sm:w-auto">
                      <button
                        onClick={() => handleEditField(field)}
                        className="flex-1 sm:flex-none px-3 sm:px-4 py-1.5 sm:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-xs sm:text-sm"
                      >
                        {t('edit')}
                      </button>
                      <button
                        onClick={() => handleDeleteField(field._id)}
                        className="flex-1 sm:flex-none px-3 sm:px-4 py-1.5 sm:py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-xs sm:text-sm"
                      >
                        {t('delete')}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

          {/* Bookings Tab */}
          {activeTab === 'bookings' && (
            <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-3 sm:gap-4">
                <div className="flex-1">
                  <h2 className="text-lg sm:text-xl font-semibold mb-2">{t('admin.allBookings')}</h2>
                  {/* Filtered Count Display */}
                  {filteredBookings.length !== bookings.length && (
                    <p className="text-sm text-gray-600">
                      <span className="font-semibold text-blue-600">{filteredBookings.length}</span>
                      {' '}{isRTL ? 'من' : 'out of'}{' '}
                      <span className="font-semibold">{bookings.length}</span>
                      {' '}{isRTL ? 'حجز' : 'bookings'}
                    </p>
                  )}
                  {filteredBookings.length === bookings.length && bookings.length > 0 && (
                    <p className="text-sm text-gray-600">
                      <span className="font-semibold">{bookings.length}</span>
                      {' '}{isRTL ? 'حجز إجمالي' : 'total bookings'}
                    </p>
                  )}
                </div>
                {/* Buttons organized in rows */}
                <div className="flex flex-col gap-2 w-full sm:w-auto">
                  {/* Row 1: View Toggle and Columns */}
                  <div className="flex gap-2 items-center">
                    <div className="flex bg-gray-100 rounded-lg p-1 gap-1">
                      <button
                        onClick={() => setBookingsView('cards')}
                        className={`px-3 py-1.5 rounded text-xs sm:text-sm font-medium transition ${
                          bookingsView === 'cards'
                            ? 'bg-white text-blue-600 shadow-sm'
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                        </svg>
                        {t('admin.viewCards')}
                      </button>
                      <button
                        onClick={() => setBookingsView('table')}
                        className={`px-3 py-1.5 rounded text-xs sm:text-sm font-medium transition ${
                          bookingsView === 'table'
                            ? 'bg-white text-blue-600 shadow-sm'
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                        {t('admin.viewTable')}
                      </button>
                    </div>
                    <button
                      onClick={() => setShowColumnSelector(true)}
                      className="px-3 sm:px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition text-xs sm:text-sm font-medium flex items-center gap-1 sm:gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                      </svg>
                      {isRTL ? 'الأعمدة' : 'Columns'}
                    </button>
                  </div>
                  {/* Row 2: Import */}
                  <div className="flex gap-2">
                    <label className="px-3 sm:px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition text-xs sm:text-sm font-medium flex items-center gap-1 sm:gap-2 cursor-pointer flex-1 sm:flex-none justify-center sm:justify-start">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      {isRTL ? 'استيراد' : 'Import'}
                      <input
                        type="file"
                        accept=".xlsx,.xls,.txt,.csv"
                        className="hidden"
                        onChange={handleExcelImport}
                      />
                    </label>
                  </div>
                  {/* Row 3: Export Buttons */}
                  <div className="flex gap-2 flex-wrap">
                    <button
                      onClick={async () => {
                        try {
                          const token = localStorage.getItem('token');
                          const response = await fetch(`${API_URL}/admin/bookings/export/csv`, {
                            headers: {
                              'Authorization': `Bearer ${token}`
                            }
                          });
                          const blob = await response.blob();
                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = 'bookings.csv';
                          document.body.appendChild(a);
                          a.click();
                          window.URL.revokeObjectURL(url);
                          document.body.removeChild(a);
                          showToast('CSV exported successfully', 'success');
                        } catch (error) {
                          showToast('Failed to export CSV', 'error');
                        }
                      }}
                      className="px-3 sm:px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition text-xs sm:text-sm font-medium flex items-center gap-1 sm:gap-2 flex-1 sm:flex-none justify-center sm:justify-start"
                    >
                      <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      CSV
                    </button>
                    <button
                      onClick={() => setShowExportModal(true)}
                      className="px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-xs sm:text-sm font-medium flex items-center gap-1 sm:gap-2 flex-1 sm:flex-none justify-center sm:justify-start"
                    >
                      <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Excel
                    </button>
                    <button
                      onClick={async () => {
                        try {
                          const token = localStorage.getItem('token');
                          const response = await fetch(`${API_URL}/admin/bookings/export/pdf`, {
                            headers: {
                              'Authorization': `Bearer ${token}`
                            }
                          });
                          const blob = await response.blob();
                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = 'bookings.pdf';
                          document.body.appendChild(a);
                          a.click();
                          window.URL.revokeObjectURL(url);
                          document.body.removeChild(a);
                          showToast('PDF exported successfully', 'success');
                        } catch (error) {
                          showToast('Failed to export PDF', 'error');
                        }
                      }}
                      className="px-3 sm:px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition text-xs sm:text-sm font-medium flex items-center gap-1 sm:gap-2 flex-1 sm:flex-none justify-center sm:justify-start"
                    >
                      <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                      PDF
                    </button>
                  </div>
                </div>
              </div>

          {/* Filters and Search */}
          <div className="mb-4 space-y-3 sm:space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
              {/* Search */}
              <div className="sm:col-span-2">
                <input
                  type="text"
                  placeholder={isRTL ? 'بحث...' : 'Search...'}
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  dir={isRTL ? 'rtl' : 'ltr'}
                />
              </div>
              {/* Status Filter */}
              <div>
                <select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  dir={isRTL ? 'rtl' : 'ltr'}
                >
                  <option value="all">{isRTL ? 'جميع الحالات' : 'All Status'}</option>
                  <option value="confirmed">{t('admin.confirmed')}</option>
                  <option value="pending">{t('admin.pending')}</option>
                  <option value="cancelled">{t('admin.cancelled')}</option>
                </select>
              </div>
              {/* Reset Filters Button */}
              <div>
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setStatusFilter('all');
                    setSortBy('date');
                    setSortOrder('desc');
                    setCurrentPage(1);
                    setColumnFilters({});
                    setColumnSorts({});
                    // Save cleared filters to config
                    if (adminConfig) {
                      const config = {
                        ...adminConfig.bookingsTableConfig,
                        columnFilters: {},
                        columnSorts: {}
                      };
                      saveAdminConfig(config);
                    }
                  }}
                  className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition font-medium text-sm"
                >
                  {isRTL ? 'إعادة تعيين' : 'Reset Filters'}
                </button>
              </div>
            </div>
          </div>

          {/* Cards View */}
          {bookingsView === 'cards' && (
            <div className="space-y-3 sm:space-y-4">
              {filteredBookings.length === 0 ? (
                <p className="text-sm sm:text-base text-gray-500 text-center py-8">{t('admin.noBookings')}</p>
              ) : (
                filteredBookings.map((booking) => (
                <div key={booking._id} className="border border-gray-200 rounded-lg p-4 sm:p-6">
                  <div className="flex flex-col sm:flex-row justify-between items-start mb-3 sm:mb-4 gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs sm:text-sm text-gray-500">
                        {booking.slot?.date ? formatDate(booking.slot.date) : 'N/A'}
                      </p>
                      <p className="text-lg sm:text-xl font-semibold text-gray-900 break-words">
                        {booking.slot?.startTime && booking.slot?.endTime 
                          ? `${formatTime(booking.slot.startTime)} - ${formatTime(booking.slot.endTime)}`
                          : 'N/A'}
                      </p>
                    </div>
                    <span className={`px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm whitespace-nowrap ${
                      booking.status === 'confirmed'
                        ? 'bg-green-100 text-green-800'
                        : booking.status === 'cancelled'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {t(`booking.status.${booking.status}`)}
                    </span>
                  </div>
                  <div className="mb-3 sm:mb-4">
                    <p className="text-xs sm:text-sm font-medium text-gray-700 mb-2">{t('admin.patientInfo')}:</p>
                    <p className="text-xs sm:text-sm text-gray-600 break-words">{t('admin.name')}: {booking.user?.name || booking.guestName || 'N/A'}</p>
                    <p className="text-xs sm:text-sm text-gray-600 break-words">{t('admin.email')}: {booking.user?.email || booking.guestEmail || 'N/A'}</p>
                    {!booking.user && (
                      <span className="inline-block mt-2 px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded">
                        {t('booking.guest')}
                      </span>
                    )}
                  </div>
                  {Object.keys(booking.bookingFields || {}).length > 0 && (
                    <div>
                      <p className="text-xs sm:text-sm font-medium text-gray-700 mb-2">{t('booking.details')}:</p>
                      <ul className="space-y-1">
                        {Object.entries(booking.bookingFields).map(([key, value]) => (
                          <li key={key} className="text-xs sm:text-sm text-gray-600 break-words">
                            <span className="font-medium">{key}:</span> {value}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {booking.status === 'confirmed' && (
                    <div className="mt-4 pt-4 border-t border-gray-200 flex gap-2 flex-wrap">
                      <button
                        onClick={() => handleEditBooking(booking)}
                        className="flex-1 sm:flex-none px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm font-medium"
                      >
                        {t('edit')}
                      </button>
                      <button
                        onClick={() => setUnbookBooking(booking)}
                        className="flex-1 sm:flex-none px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition text-sm font-medium"
                      >
                        {t('admin.unbook')}
                      </button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
          )}

          {/* Table View */}
          {bookingsView === 'table' && (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                  {/* Column Headers */}
                  <tr>
                    {getVisibleColumns().includes('date') && (
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        <div className="flex items-center gap-1">
                          <span className="flex-1">{t('admin.date')}</span>
                          <button
                            onClick={() => handleColumnSort('date')}
                            className="p-1 hover:bg-gray-200 rounded transition"
                            title={isRTL ? 'ترتيب' : 'Sort'}
                          >
                            {columnSorts['date'] === 'asc' ? '↑' : columnSorts['date'] === 'desc' ? '↓' : '⇅'}
                          </button>
                          <button
                            onClick={() => handleColumnVisibilityToggle('date')}
                            className="p-1 hover:bg-red-200 rounded transition text-red-600"
                            title={isRTL ? 'إخفاء العمود' : 'Hide Column'}
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                        <input
                          type="text"
                          placeholder={isRTL ? 'فلترة...' : 'Filter...'}
                          value={columnFilters['date'] || ''}
                          onChange={(e) => handleColumnFilterChange('date', e.target.value)}
                          className="mt-2 w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                          dir={isRTL ? 'rtl' : 'ltr'}
                        />
                      </th>
                    )}
                    {getVisibleColumns().includes('time') && (
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        <div className="flex items-center gap-1">
                          <span className="flex-1">{t('admin.time')}</span>
                          <button
                            onClick={() => handleColumnSort('time')}
                            className="p-1 hover:bg-gray-200 rounded transition"
                            title={isRTL ? 'ترتيب' : 'Sort'}
                          >
                            {columnSorts['time'] === 'asc' ? '↑' : columnSorts['time'] === 'desc' ? '↓' : '⇅'}
                          </button>
                          <button
                            onClick={() => handleColumnVisibilityToggle('time')}
                            className="p-1 hover:bg-red-200 rounded transition text-red-600"
                            title={isRTL ? 'إخفاء العمود' : 'Hide Column'}
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                        <input
                          type="text"
                          placeholder={isRTL ? 'فلترة...' : 'Filter...'}
                          value={columnFilters['time'] || ''}
                          onChange={(e) => handleColumnFilterChange('time', e.target.value)}
                          className="mt-2 w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                          dir={isRTL ? 'rtl' : 'ltr'}
                        />
                      </th>
                    )}
                    {getVisibleColumns().includes('name') && (
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        <div className="flex items-center gap-1">
                          <span className="flex-1">{t('admin.name')}</span>
                          <button
                            onClick={() => handleColumnSort('name')}
                            className="p-1 hover:bg-gray-200 rounded transition"
                            title={isRTL ? 'ترتيب' : 'Sort'}
                          >
                            {columnSorts['name'] === 'asc' ? '↑' : columnSorts['name'] === 'desc' ? '↓' : '⇅'}
                          </button>
                          <button
                            onClick={() => handleColumnVisibilityToggle('name')}
                            className="p-1 hover:bg-red-200 rounded transition text-red-600"
                            title={isRTL ? 'إخفاء العمود' : 'Hide Column'}
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                        <input
                          type="text"
                          placeholder={isRTL ? 'فلترة...' : 'Filter...'}
                          value={columnFilters['name'] || ''}
                          onChange={(e) => handleColumnFilterChange('name', e.target.value)}
                          className="mt-2 w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                          dir={isRTL ? 'rtl' : 'ltr'}
                        />
                      </th>
                    )}
                    {getVisibleColumns().includes('email') && (
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        <div className="flex items-center gap-1">
                          <span className="flex-1">{t('admin.email')}</span>
                          <button
                            onClick={() => handleColumnSort('email')}
                            className="p-1 hover:bg-gray-200 rounded transition"
                            title={isRTL ? 'ترتيب' : 'Sort'}
                          >
                            {columnSorts['email'] === 'asc' ? '↑' : columnSorts['email'] === 'desc' ? '↓' : '⇅'}
                          </button>
                          <button
                            onClick={() => handleColumnVisibilityToggle('email')}
                            className="p-1 hover:bg-red-200 rounded transition text-red-600"
                            title={isRTL ? 'إخفاء العمود' : 'Hide Column'}
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                        <input
                          type="text"
                          placeholder={isRTL ? 'فلترة...' : 'Filter...'}
                          value={columnFilters['email'] || ''}
                          onChange={(e) => handleColumnFilterChange('email', e.target.value)}
                          className="mt-2 w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                          dir={isRTL ? 'rtl' : 'ltr'}
                        />
                      </th>
                    )}
                    {getVisibleColumns().includes('status') && (
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        <div className="flex items-center gap-1">
                          <span className="flex-1">{t('admin.status')}</span>
                          <button
                            onClick={() => handleColumnSort('status')}
                            className="p-1 hover:bg-gray-200 rounded transition"
                            title={isRTL ? 'ترتيب' : 'Sort'}
                          >
                            {columnSorts['status'] === 'asc' ? '↑' : columnSorts['status'] === 'desc' ? '↓' : '⇅'}
                          </button>
                          <button
                            onClick={() => handleColumnVisibilityToggle('status')}
                            className="p-1 hover:bg-red-200 rounded transition text-red-600"
                            title={isRTL ? 'إخفاء العمود' : 'Hide Column'}
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                        <input
                          type="text"
                          placeholder={isRTL ? 'فلترة...' : 'Filter...'}
                          value={columnFilters['status'] || ''}
                          onChange={(e) => handleColumnFilterChange('status', e.target.value)}
                          className="mt-2 w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                          dir={isRTL ? 'rtl' : 'ltr'}
                        />
                      </th>
                    )}
                    {getAllFieldNames().filter(fieldName => getVisibleColumns().includes(fieldName)).map(fieldName => (
                      <th key={fieldName} className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        <div className="flex items-center gap-1">
                          <span className="flex-1 break-words">{fieldName}</span>
                          <button
                            onClick={() => handleColumnSort(fieldName)}
                            className="p-1 hover:bg-gray-200 rounded transition flex-shrink-0"
                            title={isRTL ? 'ترتيب' : 'Sort'}
                          >
                            {columnSorts[fieldName] === 'asc' ? '↑' : columnSorts[fieldName] === 'desc' ? '↓' : '⇅'}
                          </button>
                          <button
                            onClick={() => handleColumnVisibilityToggle(fieldName)}
                            className="p-1 hover:bg-red-200 rounded transition text-red-600 flex-shrink-0"
                            title={isRTL ? 'إخفاء العمود' : 'Hide Column'}
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                        <input
                          type="text"
                          placeholder={isRTL ? 'فلترة...' : 'Filter...'}
                          value={columnFilters[fieldName] || ''}
                          onChange={(e) => handleColumnFilterChange(fieldName, e.target.value)}
                          className="mt-2 w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                          dir={isRTL ? 'rtl' : 'ltr'}
                        />
                      </th>
                    ))}
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider sticky right-0 bg-gradient-to-r from-gray-50 to-gray-100">
                      {t('admin.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedBookings.length === 0 ? (
                    <tr>
                      <td colSpan={getVisibleColumns().length + 1} className="px-4 py-8 text-center text-gray-500">
                        {t('admin.noBookings')}
                      </td>
                    </tr>
                  ) : (
                    paginatedBookings.map((booking) => {
                      const fieldNames = getAllFieldNames();
                      return (
                        <tr key={booking._id} className="hover:bg-blue-50 transition-colors">
                          {getVisibleColumns().includes('date') && (
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                              {booking.slot?.date ? formatDate(booking.slot.date) : 'N/A'}
                            </td>
                          )}
                          {getVisibleColumns().includes('time') && (
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                              {booking.slot?.startTime && booking.slot?.endTime 
                                ? `${formatTime(booking.slot.startTime)} - ${formatTime(booking.slot.endTime)}`
                                : 'N/A'}
                            </td>
                          )}
                          {getVisibleColumns().includes('name') && (
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                              {booking.user?.name || booking.guestName || 'N/A'}
                              {!booking.user && (
                                <span className="ml-2 px-2 py-0.5 text-xs bg-yellow-100 text-yellow-800 rounded">
                                  {t('booking.guest')}
                                </span>
                              )}
                            </td>
                          )}
                          {getVisibleColumns().includes('email') && (
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                              {booking.user?.email || booking.guestEmail || 'N/A'}
                            </td>
                          )}
                          {getVisibleColumns().includes('status') && (
                            <td className="px-4 py-4 whitespace-nowrap">
                              <span className={`px-2 py-1 rounded-full text-xs ${
                                booking.status === 'confirmed'
                                  ? 'bg-green-100 text-green-800'
                                  : booking.status === 'cancelled'
                                  ? 'bg-red-100 text-red-800'
                                  : 'bg-yellow-100 text-yellow-800'
                              }`}>
                                {t(`booking.status.${booking.status}`)}
                              </span>
                            </td>
                          )}
                          {fieldNames.filter(fieldName => getVisibleColumns().includes(fieldName)).map(fieldName => {
                            let fieldValue = '';
                            if (booking.bookingFields) {
                              if (booking.bookingFields instanceof Map) {
                                fieldValue = booking.bookingFields.get(fieldName) || '';
                              } else if (typeof booking.bookingFields === 'object') {
                                fieldValue = booking.bookingFields[fieldName] || '';
                              }
                            }
                            return (
                              <td key={fieldName} className="px-4 py-4 text-sm text-gray-900">
                                {fieldValue}
                              </td>
                            );
                          })}
                          <td className="px-4 py-4 whitespace-nowrap text-sm sticky right-0 bg-white">
                            {booking.status === 'confirmed' && (
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleEditBooking(booking)}
                                  className="px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-xs font-medium"
                                >
                                  {t('edit')}
                                </button>
                                <button
                                  onClick={() => setUnbookBooking(booking)}
                                  className="px-3 py-1 bg-red-600 text-white rounded-lg hover:bg-red-700 transition text-xs font-medium"
                                >
                                  {t('admin.unbook')}
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mt-4 flex flex-col sm:flex-row justify-between items-center gap-3">
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-700">
                      {isRTL ? 'عناصر لكل صفحة:' : 'Items per page:'}
                    </label>
                    <select
                      value={itemsPerPage}
                      onChange={(e) => {
                        setItemsPerPage(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                      className="px-3 py-1 border border-gray-300 rounded-lg"
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-1 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      {isRTL ? 'السابق' : 'Previous'}
                    </button>
                    <span className="text-sm text-gray-700">
                      {isRTL ? `صفحة ${currentPage} من ${totalPages}` : `Page ${currentPage} of ${totalPages}`}
                    </span>
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      {isRTL ? 'التالي' : 'Next'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Unbook Confirmation Modal */}
      {unbookBooking && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" dir={isRTL ? 'rtl' : 'ltr'}>
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6" dir={isRTL ? 'rtl' : 'ltr'}>
            <div className="mb-4">
              <div className="mx-auto w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2 text-center">{t('admin.unbookConfirm')}</h2>
              <p className="text-sm text-gray-600 mb-4 text-center">{t('admin.unbookConfirmMessage')}</p>
              {unbookBooking && (
                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                  <p className="text-sm font-medium text-gray-700 mb-1">
                    {t('admin.date')}: {unbookBooking.slot?.date ? formatDate(unbookBooking.slot.date) : 'N/A'}
                  </p>
                  <p className="text-sm font-medium text-gray-700 mb-1">
                    {t('admin.time')}: {unbookBooking.slot?.startTime && unbookBooking.slot?.endTime 
                      ? `${formatTime(unbookBooking.slot.startTime)} - ${formatTime(unbookBooking.slot.endTime)}`
                      : 'N/A'}
                  </p>
                  <p className="text-sm font-medium text-gray-700">
                    {t('admin.name')}: {unbookBooking.user?.name || unbookBooking.guestName || 'N/A'}
                  </p>
                </div>
              )}
            </div>
            <div className={`flex gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <button
                onClick={() => setUnbookBooking(null)}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition font-medium"
              >
                {t('cancel')}
              </button>
              <button
                onClick={handleUnbook}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-medium"
              >
                {t('admin.unbook')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Results Modal */}
      {importResults && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" dir={isRTL ? 'rtl' : 'ltr'}>
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-bold text-gray-900">
                  {isRTL ? 'نتائج الاستيراد' : 'Import Results'}
                </h3>
                <button
                  onClick={() => setImportResults(null)}
                  className="text-gray-400 hover:text-gray-600 transition"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="mt-4 flex gap-4">
                <div className="px-4 py-2 bg-green-100 text-green-800 rounded-lg">
                  <span className="font-semibold">{importResults.passed}</span> {isRTL ? 'نجح' : 'Passed'}
                </div>
                <div className="px-4 py-2 bg-red-100 text-red-800 rounded-lg">
                  <span className="font-semibold">{importResults.failed}</span> {isRTL ? 'فشل' : 'Failed'}
                </div>
                <div className="px-4 py-2 bg-gray-100 text-gray-800 rounded-lg">
                  <span className="font-semibold">{importResults.total}</span> {isRTL ? 'إجمالي' : 'Total'}
                </div>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {isRTL ? 'الصف' : 'Row'}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {isRTL ? 'الاسم' : 'Name'}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {isRTL ? 'تاريخ الحجز' : 'Booking Date/Time'}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {isRTL ? 'الحالة' : 'Status'}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {isRTL ? 'الوصف' : 'Description'}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {importResults.results.map((result, index) => (
                    <tr key={index} className={result.status === 'failed' ? 'bg-red-50' : 'bg-green-50'}>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {result.row}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {result.name}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {result.bookingDateTime}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {result.status === 'passed' ? (
                          <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                            {isRTL ? 'نجح' : 'Passed'}
                          </span>
                        ) : (
                          <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                            {isRTL ? 'فشل' : 'Failed'}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {result.error ? (
                          <span className="text-red-600">{result.error}</span>
                        ) : (
                          <span className="text-green-600">{isRTL ? 'تم التحقق بنجاح' : 'Validation passed'}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setImportResults(null)}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium"
              >
                {isRTL ? 'إغلاق' : 'Close'}
              </button>
            </div>
          </div>
        </div>
          )}

      {/* Export Options Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" dir={isRTL ? 'rtl' : 'ltr'}>
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6" dir={isRTL ? 'rtl' : 'ltr'}>
            <div className="mb-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-gray-900">
                  {isRTL ? 'خيارات التصدير' : 'Export Options'}
                </h3>
                <button
                  onClick={() => setShowExportModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p className="text-sm text-gray-600 mb-6">
                {isRTL ? 'اختر الحقول التي تريد تصديرها' : 'Choose which fields to export'}
              </p>
            </div>
            <div className="space-y-3">
              <button
                onClick={async () => {
                  try {
                    const token = localStorage.getItem('token');
                    const response = await fetch(`${API_URL}/admin/bookings/export/excel?fields=all`, {
                      headers: {
                        'Authorization': `Bearer ${token}`
                      }
                    });
                    const blob = await response.blob();
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'bookings-all-fields.xlsx';
                    document.body.appendChild(a);
                    a.click();
                    window.URL.revokeObjectURL(url);
                    document.body.removeChild(a);
                    showToast('Excel exported successfully', 'success');
                    setShowExportModal(false);
                  } catch (error) {
                    showToast('Failed to export Excel', 'error');
                  }
                }}
                className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium text-left flex items-center justify-between"
              >
                <span>{isRTL ? 'تصدير جميع الحقول' : 'Export All Fields'}</span>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </button>
              <button
                onClick={async () => {
                  try {
                    const token = localStorage.getItem('token');
                    const visibleFields = getVisibleColumns().join(',');
                    const response = await fetch(`${API_URL}/admin/bookings/export/excel?fields=visible&columns=${visibleFields}`, {
                      headers: {
                        'Authorization': `Bearer ${token}`
                      }
                    });
                    const blob = await response.blob();
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'bookings-visible-fields.xlsx';
                    document.body.appendChild(a);
                    a.click();
                    window.URL.revokeObjectURL(url);
                    document.body.removeChild(a);
                    showToast('Excel exported successfully', 'success');
                    setShowExportModal(false);
                  } catch (error) {
                    showToast('Failed to export Excel', 'error');
                  }
                }}
                className="w-full px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition font-medium text-left flex items-center justify-between"
              >
                <span>{isRTL ? 'تصدير الحقول المرئية فقط' : 'Export Visible Fields Only'}</span>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </button>
            </div>
            <div className="mt-6 pt-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setShowExportModal(false)}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition font-medium"
              >
                {isRTL ? 'إلغاء' : 'Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Column Selector Modal */}
      {showColumnSelector && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" dir={isRTL ? 'rtl' : 'ltr'}>
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-bold text-gray-900">
                  {isRTL ? 'تخصيص الأعمدة' : 'Customize Columns'}
                </h3>
                <button
                  onClick={() => setShowColumnSelector(false)}
                  className="text-gray-400 hover:text-gray-600 transition"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p className="text-sm text-gray-600 mt-2">
                {isRTL ? 'اختر الأعمدة التي تريد عرضها في الجدول' : 'Select which columns to display in the table'}
              </p>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">{isRTL ? 'الأعمدة الأساسية' : 'Default Columns'}</h4>
                  <div className="space-y-2">
                    {['date', 'time', 'name', 'email', 'status'].map(columnKey => {
                      const isVisible = getVisibleColumns().includes(columnKey);
                      const columnLabels = {
                        date: t('admin.date'),
                        time: t('admin.time'),
                        name: t('admin.name'),
                        email: t('admin.email'),
                        status: t('admin.status')
                      };
                      return (
                        <label key={columnKey} className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition">
                          <input
                            type="checkbox"
                            checked={isVisible}
                            onChange={() => handleColumnVisibilityToggle(columnKey)}
                            className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                          />
                          <span className="ml-3 text-sm font-medium text-gray-900">{columnLabels[columnKey]}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
                {getAllFieldNames().length > 0 && (
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">
                      {isRTL ? 'الحقول المخصصة' : 'Custom Fields'} ({getAllFieldNames().length})
                    </h4>
                    <div className="space-y-2 max-h-96 overflow-y-auto pr-2 custom-scrollbar">
                      {getAllFieldNames().map(fieldName => {
                        const isVisible = getVisibleColumns().includes(fieldName);
                        return (
                          <label key={fieldName} className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition">
                            <input
                              type="checkbox"
                              checked={isVisible}
                              onChange={() => handleColumnVisibilityToggle(fieldName)}
                              className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                            />
                            <span className="ml-3 text-sm font-medium text-gray-900 break-words">{fieldName}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}
                {getAllFieldNames().length === 0 && bookings.length > 0 && (
                  <div className="text-sm text-gray-500 italic p-3 bg-yellow-50 rounded-lg">
                    {isRTL ? 'لا توجد حقول مخصصة في الحجوزات الحالية' : 'No custom fields found in current bookings'}
                  </div>
                )}
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setShowColumnSelector(false)}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium"
              >
                {isRTL ? 'إغلاق' : 'Close'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Booking Modal */}
      {editingBooking && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" dir={isRTL ? 'rtl' : 'ltr'}>
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-bold text-gray-900">
                  {isRTL ? 'تعديل الحجز' : 'Edit Booking'}
                </h3>
                <button
                  onClick={() => {
                    setEditingBooking(null);
                    setEditBookingDate('');
                    setEditBookingSlots([]);
                    setSelectedSlotForEdit(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 transition"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="mt-4 bg-gray-50 rounded-lg p-4">
                <p className="text-sm font-medium text-gray-700 mb-1">
                  {isRTL ? 'المريض' : 'Patient'}: {editingBooking.user?.name || editingBooking.guestName || 'N/A'}
                </p>
                <p className="text-sm font-medium text-gray-700 mb-1">
                  {isRTL ? 'الحجز الحالي' : 'Current Booking'}: {editingBooking.slot?.date 
                    ? `${formatDate(editingBooking.slot.date)} ${editingBooking.slot.startTime && editingBooking.slot.endTime ? `${formatTime(editingBooking.slot.startTime)} - ${formatTime(editingBooking.slot.endTime)}` : ''}`
                    : 'N/A'}
                </p>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {isRTL ? 'اختر التاريخ' : 'Select Date'}
                  </label>
                  <DatePicker
                    value={editBookingDate}
                    onChange={handleEditBookingDateChange}
                    min={new Date().toISOString().split('T')[0]}
                    placeholder={isRTL ? 'اختر التاريخ' : 'Select Date'}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {isRTL ? 'اختر الوقت' : 'Select Time Slot'}
                  </label>
                  {editBookingSlots.length === 0 ? (
                    <p className="text-sm text-gray-500 py-4 text-center">
                      {isRTL ? 'لا توجد مواعيد متاحة لهذا التاريخ' : 'No slots available for this date'}
                    </p>
                  ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {editBookingSlots
                        .sort((a, b) => {
                          const timeA = a.startTime.split(':').map(Number);
                          const timeB = b.startTime.split(':').map(Number);
                          return timeA[0] * 60 + timeA[1] - (timeB[0] * 60 + timeB[1]);
                        })
                        .map((slot) => {
                          const isCurrentSlot = slot._id === editingBooking.slot._id;
                          const isSelected = selectedSlotForEdit === slot._id;
                          return (
                            <button
                              key={slot._id}
                              onClick={() => {
                                if (slot.isAvailable || isCurrentSlot) {
                                  setSelectedSlotForEdit(slot._id);
                                }
                              }}
                              disabled={!slot.isAvailable && !isCurrentSlot}
                              className={`w-full py-3 px-4 rounded-lg transition-all text-left ${
                                isSelected
                                  ? 'bg-blue-600 text-white border-2 border-blue-700 shadow-md'
                                  : isCurrentSlot
                                  ? 'bg-blue-100 text-blue-800 border-2 border-blue-300'
                                  : slot.isAvailable
                                  ? 'bg-gray-100 text-gray-900 hover:bg-gray-200 border-2 border-transparent hover:border-blue-300'
                                  : 'bg-gray-200 text-gray-500 cursor-not-allowed opacity-60 border-2 border-transparent'
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <span className="font-semibold">
                                  {formatTime(slot.startTime)} - {formatTime(slot.endTime)}
                                </span>
                                {isCurrentSlot && (
                                  <span className="text-xs bg-white text-blue-600 px-2 py-1 rounded-full">
                                    {isRTL ? 'الحالي' : 'Current'}
                                  </span>
                                )}
                                {isSelected && !isCurrentSlot && (
                                  <span className="text-xs bg-white text-blue-600 px-2 py-1 rounded-full">
                                    {isRTL ? 'محدد' : 'Selected'}
                                  </span>
                                )}
                                {!slot.isAvailable && !isCurrentSlot && (
                                  <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full">
                                    {t('calendar.booked')}
                                  </span>
                                )}
                              </div>
                            </button>
                          );
                        })}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setEditingBooking(null);
                  setEditBookingDate('');
                  setEditBookingSlots([]);
                  setSelectedSlotForEdit(null);
                }}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition font-medium"
              >
                {t('cancel')}
              </button>
              <button
                onClick={handleSaveBookingEdit}
                disabled={editBookingLoading || !editingBooking || !selectedSlotForEdit}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {editBookingLoading ? (isRTL ? 'جاري الحفظ...' : 'Saving...') : (isRTL ? 'حفظ' : 'Save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
